
import React, { useState } from "react";
import { Send, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  email: z.string().email({ message: "Please enter a valid email address." }),
  phone: z.string().optional(),
  company: z.string().optional(),
  productInterest: z.string(),
  message: z.string().min(5, { message: "Please provide a message with at least 5 characters." })
});

type FormValues = z.infer<typeof formSchema>;

const ContactForm = () => {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      company: "",
      productInterest: "tablets",
      message: ""
    }
  });

  const onSubmit = async (formData: FormValues) => {
    setFormStatus('submitting');
    
    try {
      // Format the phone number correctly for the database
      let phoneNumberAsNum = null;
      if (formData.phone && formData.phone.trim() !== '') {
        const phoneNumber = formData.phone.replace(/\D/g, '');
        if (phoneNumber.length > 0) {
          phoneNumberAsNum = parseInt(phoneNumber, 10);
        }
      }

      // Match column names exactly to the Supabase table structure
      const submitData = {
        "Full Name": formData.name,
        "Email Address": formData.email,
        "Phone Number": phoneNumberAsNum,
        "Company Name": formData.company || null,
        product_interest: formData.productInterest,
      message: formData.message
    };

    const { error } = await supabase
        .from("TRUECORE GADGETS")
        .insert([submitData]);
      
      if (error) {
        setFormStatus('error');
        toast({
          title: "Submission failed",
          description: `Error: ${error.message || "Please try again later."}`,
          variant: "destructive"
        });
        return;
      }
      
      setFormStatus('success');
      toast({
        title: "Thank you!",
        description: "Your contact request has been submitted successfully."
      });
      
      setTimeout(() => {
        form.reset();
        setFormStatus('idle');
      }, 3000);
      
    } catch (err) {
      setFormStatus('error');
      toast({
        title: "Submission failed",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="bg-white p-8 rounded-xl shadow-md border border-border/50">
      <h3 className="text-2xl font-bold mb-6">Request a Consultation</h3>
      {formStatus === 'success' ? (
        <div className="flex flex-col items-center justify-center h-full py-12">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h4 className="text-xl font-bold mb-2">Thank You!</h4>
          <p className="text-center text-muted-foreground">
            Your message has been sent successfully. We'll get back to you within 24 hours.
          </p>
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address *</FormLabel>
                    <FormControl>
                      <Input placeholder="john@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <Input placeholder="+91 98765 43210" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="company"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Company Name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="productInterest"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Product Interest *</FormLabel>
                  <FormControl>
                    <select
                      className="w-full px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                      {...field}
                    >
                      <option value="tablets">Custom Tablets</option>
                      <option value="pcs">Custom PCs</option>
                      <option value="desktops">Custom Desktops</option>
                      <option value="business">Business Solutions</option>
                      <option value="other">Other</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Message *</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Tell us about your requirements..."
                      rows={4}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <Button
              type="submit"
              disabled={formStatus === 'submitting'}
              className="tech-btn-primary w-full flex items-center justify-center"
            >
              {formStatus === 'submitting' ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  Send Message
                  <Send className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
};

export default ContactForm;
